/**
 * Единообразные состояния — §13.2: «Загрузка, пусто, ошибка, нет доступа —
 * одинаково во всех трёх».
 *
 * Экран отказа — §6.4, и текст в нём тот же самый, что бот отправляет в
 * Telegram (`toscana_platform/identity/denial.py`). Совпадение не
 * случайное: человек, которому отказали и там и там, должен видеть одно и
 * то же — иначе он решит, что где-то доступ всё-таки есть, просто он не
 * туда нажал.
 *
 * Причина отказа не показывается никогда. Сервер её и не присылает.
 */

import type { ReactNode } from 'react';
import { ApiError } from './api';
import { Button } from './ui';

/** Тот же текст, что и в `identity/denial.py` (§6.4). */
export const DENIAL_TEXT = 'У вас нет доступа к этому сервису.';
export const REQUEST_ACCESS_BUTTON_TEXT = 'Запросить доступ';

export function Loading({ text = 'Загружаю…' }: { text?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <div className="spinner" />
      <p className="state__text">{text}</p>
    </div>
  );
}

export function Empty({ title, text }: { title: string; text?: ReactNode }) {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      {text && <p className="state__text">{text}</p>}
    </div>
  );
}

export function ErrorState({
  onRetry,
  text,
  title,
  error,
}: {
  onRetry?: () => void;
  text?: string;
  /** Заголовок вместо «Не удалось загрузить данные»: у части отказов
   *  причина известна точно, и общая формулировка отправляет человека
   *  чинить не то (например, устаревший `initData` в Mini App). */
  title?: string;
  /**
   * Сам отказ, если он есть.
   *
   * §6 `docs/interface-principles.md`: ошибка говорит, что не получилось
   * и что делать. Сервер на `400` присылает причину словами — «период
   * задаётся двумя датами сразу», «PDF без текстового слоя», — и раньше
   * этот текст выбрасывался, а человек читал «похоже, сервер
   * недоступен» и шёл искать поломку вместо того, чтобы исправить
   * введённое. Отказ в доступе причины не содержит (§6.4) и потому
   * показывается общей формулировкой, как и прежде.
   */
  error?: unknown;
}) {
  const detail = error instanceof ApiError ? error.detail : null;
  return (
    <div className="state" role="alert">
      <p className="state__title">
        {title ?? (detail ? 'Не получилось' : 'Не удалось загрузить данные')}
      </p>
      <p className="state__text">
        {text ??
          detail ??
          'Похоже, сервер сейчас недоступен. Попробуйте обновить — если не поможет, ' +
            'сообщите разработчику.'}
      </p>
      {onRetry && (
        <Button variant="primary" onClick={onRetry}>
          Обновить
        </Button>
      )}
    </div>
  );
}

/**
 * Экран отказа §6.4. Не пустой дашборд, не «раздел в разработке» — прямой
 * ответ и единственное осмысленное действие: запросить доступ у владельца
 * через бота (§8.3, шаги 3–4).
 */
export function Denied({ botUsername }: { botUsername?: string }) {
  return (
    <div className="state" role="alert">
      <p className="state__title">{DENIAL_TEXT}</p>
      <p className="state__text">
        Доступ выдаёт владелец поимённо и отдельно к каждому сервису. Чтобы его запросить,
        напишите боту — он задаст три коротких вопроса и передаст заявку.
      </p>
      {botUsername && (
        <a
          className="button button--primary"
          href={`https://t.me/${botUsername}`}
          target="_blank"
          rel="noreferrer"
        >
          {REQUEST_ACCESS_BUTTON_TEXT}
        </a>
      )}
    </div>
  );
}
